import { Controller } from "@hotwired/stimulus";
import { makeMatcher } from "../utils/text_match";

// 4-step wizard for creating an Interaction (with multi-type participants):
//   0. Type        — radio cards for interaction types
//   1. Participants — three tabs (Contact / Membre / Adhérent), each with a
//                     local search filter and checkboxes; counters update live
//   2. Date         — quick chips (today / yesterday / day-before) + date picker
//   3. Content      — résumé + description, with an inline recap above
//
// On submit, the form posts to the top-level /juniors/:id/interactions
// endpoint with `participants[contact][]`, `participants[membre][]`,
// `participants[adherent][]` arrays (controller persists one
// InteractionParticipant per ID).
export default class extends Controller {
  static targets = [
    "form", "step", "progressStep", "progressCurrent", "progressTotal",
    "nextBtn", "prevBtn", "submitBtn", "error",
    "typeRadio",
    "participantBox", "participantPane",
    "contactCount", "membreCount", "adherentCount",
    "participantsSummary",
    "dateField", "resumeField", "descriptionField",
    "recap", "recapType", "recapParticipants", "recapDate",
  ];

  static values = {
    current: { type: Number, default: 0 },
    total:   { type: Number, default: 4 },
    juniorId: Number,
    etudeId:  Number,
    baseUrl:  String,
  };

  connect() {
    this.progressTotalTarget.textContent = this.totalValue;
    this.participantBoxTargets.forEach((box) => {
      box.addEventListener("change", () => this._refreshParticipantCounts());
    });
    this._refreshParticipantCounts();
    this._render();
  }

  // ---- Navigation -----------------------------------------------------

  next(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    if (this.currentValue >= this.totalValue - 1) return;
    this.currentValue += 1;
    this._render();
  }

  prev(event) {
    event.preventDefault();
    if (this.currentValue <= 0) return;
    this.currentValue -= 1;
    this._render();
  }

  pickDate(event) {
    event.preventDefault();
    const date = event.currentTarget.dataset.date;
    this.dateFieldTarget.value = date;
    this.element.querySelectorAll(".esm_wizard__date-chip").forEach((c) => {
      c.classList.toggle("esm_wizard__date-chip--active", c === event.currentTarget);
    });
  }

  switchParticipantTab(event) {
    event.preventDefault();
    const tab = event.currentTarget.dataset.tab;
    this.element.querySelectorAll(".esm_wizard__participants-tab").forEach((b) => {
      b.classList.toggle("esm_wizard__participants-tab--active", b.dataset.tab === tab);
    });
    this.participantPaneTargets.forEach((p) => {
      const isActive = p.dataset.tab === tab;
      p.classList.toggle("esm_wizard__participants-pane--active", isActive);
      p.hidden = !isActive;
    });
  }

  filterParticipants(event) {
    // Recherche par mots : « Olivia Bironneau » doit retrouver la ligne
    // « BIRONNEAU Olivia », quel que soit l'ordre de saisie.
    const matcher = makeMatcher(event.currentTarget.value);
    const pane = event.currentTarget.closest(".esm_wizard__participants-pane");
    pane.querySelectorAll(".esm_wizard__participant-row").forEach((row) => {
      row.style.display = matcher(row.dataset.search || "") ? "" : "none";
    });
  }

  submit(event) {
    event.preventDefault();
    if (!this._validateCurrent()) return;
    const totalParticipants = this._participantCount();
    if (totalParticipants === 0) {
      this._showError("Choisis au moins un participant.");
      this.currentValue = 1;
      this._render();
      return;
    }
    this.submitBtnTarget.disabled = true;
    this.submitBtnTarget.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Enregistrement…';
    this.formTarget.submit();
  }

  // ---- Rendering ------------------------------------------------------

  _render() {
    this.stepTargets.forEach((s) => {
      s.classList.toggle("esm_wizard__step--active", Number(s.dataset.step) === this.currentValue);
    });
    this.progressStepTargets.forEach((p) => {
      const i = Number(p.dataset.step);
      p.classList.toggle("esm_wizard__progress-step--active", i === this.currentValue);
      p.classList.toggle("esm_wizard__progress-step--done",   i < this.currentValue);
    });
    this.progressCurrentTarget.textContent = this.currentValue + 1;

    this.prevBtnTarget.hidden = this.currentValue === 0;

    const isLast = this.currentValue === this.totalValue - 1;
    this.nextBtnTarget.hidden = isLast;
    this.submitBtnTarget.hidden = !isLast;

    if (isLast) {
      this._fillRecap();
      this.recapTarget.hidden = false;
    } else if (this.hasRecapTarget) {
      this.recapTarget.hidden = true;
    }
    this._hideError();
  }

  _validateCurrent() {
    this._hideError();
    if (this.currentValue === 0) {
      const checked = this.typeRadioTargets.find((r) => r.checked);
      if (!checked) {
        this._showError("Choisis un type d'interaction.");
        return false;
      }
      return true;
    }
    if (this.currentValue === 1) {
      if (this._participantCount() === 0) {
        this._showError("Choisis au moins un participant.");
        return false;
      }
      return true;
    }
    if (this.currentValue === 2) {
      if (!this.dateFieldTarget.value) {
        this._showError("Précise une date.");
        return false;
      }
      return true;
    }
    if (this.currentValue === 3) {
      if (!this.resumeFieldTarget.value.trim()) {
        this._showError("Saisis un résumé court.");
        this.resumeFieldTarget.focus();
        return false;
      }
      if (!this.descriptionFieldTarget.value.trim()) {
        this._showError("Ajoute un compte rendu.");
        this.descriptionFieldTarget.focus();
        return false;
      }
      return true;
    }
    return true;
  }

  // ---- Participants ---------------------------------------------------

  _participantCount() {
    return this.participantBoxTargets.filter((b) => b.checked).length;
  }

  _refreshParticipantCounts() {
    const counts = { contact: 0, membre: 0, adherent: 0 };
    this.participantBoxTargets.forEach((b) => {
      if (b.checked) counts[b.dataset.type] = (counts[b.dataset.type] || 0) + 1;
    });
    if (this.hasContactCountTarget)  this.contactCountTarget.textContent  = counts.contact;
    if (this.hasMembreCountTarget)   this.membreCountTarget.textContent   = counts.membre;
    if (this.hasAdherentCountTarget) this.adherentCountTarget.textContent = counts.adherent;

    const total = counts.contact + counts.membre + counts.adherent;
    if (this.hasParticipantsSummaryTarget) {
      if (total > 0) {
        const labels = [];
        if (counts.contact)  labels.push(`${counts.contact} contact${counts.contact > 1 ? "s" : ""}`);
        if (counts.membre)   labels.push(`${counts.membre} membre${counts.membre > 1 ? "s" : ""}`);
        if (counts.adherent) labels.push(`${counts.adherent} adhérent${counts.adherent > 1 ? "s" : ""}`);
        this.participantsSummaryTarget.textContent = `${total} participant${total > 1 ? "s" : ""} : ${labels.join(", ")}`;
        this.participantsSummaryTarget.hidden = false;
      } else {
        this.participantsSummaryTarget.hidden = true;
      }
    }
  }

  _participantLabels() {
    return this.participantBoxTargets.filter((b) => b.checked).map((b) => b.dataset.label);
  }

  // ---- Recap ----------------------------------------------------------

  _fillRecap() {
    const type = this.typeRadioTargets.find((r) => r.checked);
    const labels = this._participantLabels();
    this.recapTypeTarget.textContent = type?.dataset.label || "—";
    this.recapParticipantsTarget.textContent =
      labels.length > 3
        ? `${labels.slice(0, 3).join(", ")} (+${labels.length - 3})`
        : (labels.join(", ") || "—");
    const dateStr = this.dateFieldTarget.value;
    this.recapDateTarget.textContent = dateStr ? this._formatFR(dateStr) : "—";
  }

  // ---- Helpers --------------------------------------------------------

  _formatFR(iso) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  _showError(msg) {
    this.errorTarget.textContent = msg;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    this.errorTarget.hidden = true;
    this.errorTarget.textContent = "";
  }
}
